import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import type { SanityEmailCaptureBlock } from '@lib/sanity/types';

const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address')
});

type EmailFormValues = z.infer<typeof emailSchema>;

type EmailCaptureBlockProps = SanityEmailCaptureBlock & {
  onSubmit: (email: string) => void;
  isLoading: boolean;
  isSubmitted: boolean;
};

const EmailCaptureBlock: FC<EmailCaptureBlockProps> = ({
  heading,
  subheading,
  placeholderText,
  buttonText,
  consentLabelText,
  privacyPolicyUrl,
  onSubmit,
  isLoading,
  isSubmitted
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    mode: 'onChange'
  });

  const [isInputFocused, setIsInputFocused] = useState(false);

  const handleFormSubmit = (data: EmailFormValues) => {
    onSubmit(data.email);
  };

  if (isSubmitted) {
    return (
      <section className="flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary-600">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FAFAF5"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2
            className="text-center font-heading font-black text-neutral-800"
            style={{
              fontSize: 'clamp(1.5rem, 4vw, 2rem)',
              lineHeight: 1.15
            }}
          >
            You&rsquo;re in.
          </h2>
          <p className="mt-2 text-sm font-body text-neutral-500">
            Hang tight, we&rsquo;re pulling your result together.
          </p>
        </div>
      </section>
    );
  }

  const hasError = Boolean(errors.email);
  const isButtonDisabled = isLoading || !isValid;
  const borderColour = hasError
    ? '#009DE5'
    : isInputFocused
    ? '#009DE5'
    : '#E5E0D8';

  return (
    <section className="flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-sm text-center">
        <h2
          className="mb-2 text-center font-heading font-black text-neutral-800"
          style={{
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
            lineHeight: 1.15
          }}
        >
          {heading}
        </h2>
        {subheading && (
          <p className="mb-8 text-sm leading-normal font-body text-neutral-500">
            {subheading}
          </p>
        )}

        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          noValidate
          className="text-left"
        >
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={placeholderText}
            {...register('email')}
            onFocus={() => setIsInputFocused(true)}
            onBlurCapture={() => setIsInputFocused(false)}
            aria-invalid={hasError}
            aria-describedby={hasError ? 'email-error' : undefined}
            className="w-full rounded-md border-2 bg-white px-[18px] py-3.5 text-sm text-neutral-800 font-body outline-none transition-all"
            style={{
              borderColor: borderColour
            }}
          />

          {hasError && (
            <p
              id="email-error"
              className="mt-2 text-sm text-primary-600 font-body"
            >
              {errors.email?.message}
            </p>
          )}

          {consentLabelText && (
            <p className="mt-5 text-sm leading-normal text-neutral-500 font-body">
              {consentLabelText}
              {privacyPolicyUrl && (
                <>
                  {' '}
                  <a
                    href={privacyPolicyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-neutral-800 underline underline-offset-2"
                  >
                    Privacy policy
                  </a>
                  .
                </>
              )}
            </p>
          )}

          <button
            type="submit"
            disabled={isButtonDisabled}
            className={`mt-6 w-full rounded px-8 py-4 text-sm font-semibold tracking-wide font-body transition-all duration-300 ${
              isButtonDisabled
                ? 'cursor-not-allowed bg-neutral-300 text-neutral-500'
                : 'cursor-pointer bg-primary-600 text-white'
            }`}
            style={{
              boxShadow: isButtonDisabled
                ? 'none'
                : '0 4px 16px rgba(0, 157, 229, 0.3)'
            }}
          >
            {isLoading ? 'Sending…' : buttonText}
          </button>
        </form>
      </div>
    </section>
  );
};

export default EmailCaptureBlock;
